import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ChevronDown, ChevronRight, FileText, Download,
  Building2, Users, Shield, BookOpen, BarChart3, Presentation,
  TrendingUp, Bell, Menu, X, ExternalLink, Mail, Phone, Archive, Mic
} from 'lucide-react';
import PageSEO from './PageSEO';
import MATERIAL_DOCS from '../data/materialDocuments.json';

/* ─── NAV TREE STRUCTURE ─── */
const NAV_TREE = [
  {
    id: 'corporate-governance',
    label: 'Corporate Governance',
    icon: Building2,
    children: [
      { id: 'corporate-profile', label: 'Corporate Profile' },
      { id: 'bod-committees', label: 'BOD & Committees' },
      { id: 'codes-policies', label: 'Codes & Policies' },
    ],
  },
  {
    id: 'offer-document',
    label: 'Offer Document',
    icon: BookOpen,
    children: [
      { id: 'offer-draft-prospectus', label: 'Draft Abridged Prospectus' },
      { id: 'offer-addendum-drhp', label: 'Addendum to Draft Abridged Prospectus' },
      { id: 'offer-rhp', label: 'RHP' },
      { id: 'offer-prospectus', label: 'Prospectus' },
      { id: 'offer-abridged', label: 'Abridged Prospectus' },
      { id: 'offer-gid', label: 'General Information Document (GID)' },
      { id: 'offer-issue-opening', label: 'Issue Opening Advertisement' },
      { id: 'offer-allotment', label: 'Basis of Allotment Advertisement' },
      { id: 'offer-public-announcement', label: 'Public Announcement' },
      { id: 'offer-public-announcement-secondary', label: 'Public Announcement – Secondary Sale' },
    ],
  },
  {
    id: 'financials',
    label: 'Financials',
    icon: BarChart3,
    children: [
      { id: 'results-reports', label: 'Results & Reports' },
      { id: 'audited-financials', label: 'Audited Financials' },
      { id: 'annual-reports', label: 'Annual Reports' },
      { id: 'annual-returns', label: 'Annual Returns' },
    ],
  },
  { id: 'investor-presentation', label: 'Investor Presentation', icon: Presentation },
  { id: 'earnings-call', label: 'Earnings Call', icon: Mic },
  { id: 'stock-exchange', label: 'Stock Exchange', icon: TrendingUp },
  {
    id: 'disclosures',
    label: 'Other Disclosures',
    icon: FileText,
    children: [
      { id: 'disclosures-reg30', label: 'Disclosures – Reg. 30' },
      { id: 'material-creditors', label: 'Material Creditors' },
    ],
  },
  {
    id: 'material',
    label: 'Material Contracts & Documents',
    icon: Archive,
    children: [
      { id: 'material-contracts', label: 'Material Contracts' },
      { id: 'material-documents', label: 'Material Documents' },
    ],
  },
  {
    id: 'notice-updates',
    label: 'Notice & Updates',
    icon: Bell,
    children: [
      { id: 'shareholding-pattern', label: 'Shareholding Pattern' },
      { id: 'board-meetings', label: 'Board Meetings' },
      { id: 'general-meetings', label: 'General Meetings' },
      { id: 'notice', label: 'Notice' },
    ],
  },
  { id: 'grievance-redressal', label: 'Investor Grievance Redressal', icon: Shield },
  {
    id: 'group-company',
    label: 'Group Company',
    icon: Users,
    children: [
      { id: 'group-silvertraq', label: 'Silvertraq International' },
    ],
  },
];

/* ─── DATA ─── */
const POLICIES = [
  { name: 'Nomination and Remuneration Policy', href: '/api/uploads/ir/policy_nomination_remuneration.pdf' },
  { name: 'Code of Conduct of Board & Senior Management', href: '/api/uploads/ir/policy_code_of_conduct_board.pdf' },
  { name: 'Policy of Making Payments to Non-Executive Directors', href: '/api/uploads/ir/policy_payments_non_executive_directors.pdf' },
  { name: 'Terms & Conditions of Appointment of Independent Directors', href: '/api/uploads/ir/policy_terms_independent_directors.pdf' },
  { name: 'Corporate Social Responsibility Committee', href: '/api/uploads/ir/policy_corporate_social_responsibility.pdf' },
  { name: 'Policy for Determination & Disclosure of Materiality of Events or Information', href: '/api/uploads/ir/policy_determination_materiality.pdf' },
  { name: 'Policy on Code of Practices and Procedures for Fair Disclosure of UPSI', href: '/api/uploads/ir/policy_fair_disclosure_upsi.pdf' },
  { name: 'Policy on Materiality of Related Party Transactions', href: '/api/uploads/ir/policy_materiality_related_party_transactions.pdf' },
  { name: 'Policy for Prevention of Insider Trading', href: '/api/uploads/ir/policy_prevention_insider_trading.pdf' },
  { name: 'Whistle Blower Policy', href: '/api/uploads/ir/policy_whistle_blower.pdf' },
  { name: 'Archival Policy', href: '/api/uploads/ir/policy_archival.pdf' },
  { name: 'Policy for Preservation of Documents', href: '/api/uploads/ir/policy_preservation_of_documents.pdf' },
  { name: 'Policy on Prevention of Sexual Harassment', href: '/api/uploads/ir/policy_prevention_sexual_harassment.pdf' },
  { name: 'Familiarisation Programme for Independent Directors', href: '/api/uploads/ir/policy_familiarisation_programme.pdf' },
  { name: 'Policy on Identification of Group Companies, Material Creditors and Material Litigations', href: '/api/uploads/ir/policy_identification_group_companies.pdf' },
];

const ANNUAL_REPORTS = [
  { year: '2024-25', href: '/api/uploads/ir/annual_report_2024-25.pdf' },
  { year: '2023-24', href: '/api/uploads/ir/annual_report_2023-24.pdf' },
  { year: '2022-23', href: '/api/uploads/ir/annual_report_2022-23.pdf' },
  { year: '2021-22', href: '/api/uploads/ir/annual_report_2021-22.pdf' },
  { year: '2020-21', href: '/api/uploads/ir/annual_report_2020-21.pdf' },
  { year: '2019-20', href: '/api/uploads/ir/annual_report_2019-20.pdf' },
  { year: '2018-19', href: '/api/uploads/ir/annual_report_2018-19.pdf' },
];
const ANNUAL_RETURNS = [
  { year: '2024-25', href: '/api/uploads/ir/annual-return-2024-25.pdf' },
  { year: '2023-24', href: '/api/uploads/ir/annual-return-2023-24.pdf' },
  { year: '2022-23', href: '/api/uploads/ir/annual-return-2022-23.pdf' },
  { year: '2021-22', href: '/api/uploads/ir/annual-return-2021-22.pdf' },
  { year: '2020-21', href: '/api/uploads/ir/annual-return-2020-21.pdf' },
  { year: '2019-20', href: '/api/uploads/ir/annual-return-2019-20.pdf' },
  { year: '2018-19', href: '/api/uploads/ir/annual-return-2018-19.pdf' },
];

/* Material Contracts & Documents (from committed manifest; files live in Object Storage) */
const MATERIAL_CONTRACTS = MATERIAL_DOCS.filter(d => d.group === 'contracts').sort((a, b) => a.order - b.order);
const MATERIAL_DOCUMENTS = MATERIAL_DOCS.filter(d => d.group === 'documents').sort((a, b) => a.order - b.order);
const MATERIAL_INDEX = MATERIAL_DOCS.filter(d => d.group === 'index').sort((a, b) => a.order - b.order);
const AUDITED_FINANCIALS = MATERIAL_DOCS.filter(d => d.title.startsWith('Audited Financial Statements')).sort((a, b) => a.order - b.order);
const materialHref = (d) => `/api/material-documents/file/${d.path}`;
const materialSubtitle = (d) => (d.desc && d.desc.length ? d.desc : formatSize(d.size));
const irFile = (name) => `/api/uploads/ir/${encodeURIComponent(name)}`;
const formatSize = (b) => {
  if (!b) return '';
  const mb = b / 1048576;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
};

/* ─── REUSABLE COMPONENTS ─── */
const DocRow = ({ title, subtitle, testId, href }) => {
  const content = (
    <div
      data-testid={testId}
      className="group flex items-center justify-between py-3.5 px-4 rounded-lg transition-all duration-200"
      style={{ background: 'transparent', border: '1px solid transparent', cursor: href ? 'pointer' : 'default' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(200,16,46,0.03)'; e.currentTarget.style.borderColor = '#D4CFC7'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div style={{ width: 32, height: 32, borderRadius: 6, background: 'rgba(200,16,46,0.06)', flexShrink: 0 }} className="flex items-center justify-center">
          <FileText size={14} color="#C8102E" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="font-display text-sm font-semibold break-words" style={{ color: '#1C1C1E' }}>{title}</p>
          {subtitle && <p className="font-mono text-xs mt-0.5" style={{ color: '#6B6B6F', fontSize: '0.6rem', letterSpacing: '0.05em' }}>{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 opacity-40 group-hover:opacity-100 transition-opacity shrink-0 ml-3">
        <span className="font-mono hidden sm:inline" style={{ fontSize: '0.55rem', color: '#C8102E', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Download</span>
        <Download size={14} color="#C8102E" />
      </div>
    </div>
  );
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{content}</a>;
  return content;
};

const AudioRow = ({ title, subtitle, src, testId }) => (
  <a href={src} download data-testid={testId} className="group block py-3.5 px-4 rounded-lg transition-colors duration-200 hover:bg-[rgba(200,16,46,0.03)]" style={{ border: '1px solid transparent', textDecoration: 'none' }}>
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div style={{ width: 32, height: 32, borderRadius: 6, background: 'rgba(200,16,46,0.06)', flexShrink: 0 }} className="flex items-center justify-center">
          <Mic size={14} color="#C8102E" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="font-display text-sm font-semibold break-words" style={{ color: '#1C1C1E' }}>{title}</p>
          {subtitle && <p className="font-mono text-xs mt-0.5" style={{ color: '#6B6B6F', fontSize: '0.6rem', letterSpacing: '0.05em' }}>{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 opacity-40 group-hover:opacity-100 transition-opacity shrink-0 ml-3">
        <span className="font-mono hidden sm:inline" style={{ fontSize: '0.55rem', color: '#C8102E', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Download</span>
        <Download size={14} color="#C8102E" />
      </div>
    </div>
  </a>
);

const SectionCard = ({ title, description, children, testId }) => (
  <div data-testid={testId} className="mb-2">
    <h3 className="font-display text-lg font-bold mb-1" style={{ color: '#1C1C1E' }}>{title}</h3>
    {description && <p className="font-body text-sm mb-5" style={{ color: '#6B6B6F', lineHeight: 1.7 }}>{description}</p>}
    <div className="space-y-1">{children}</div>
  </div>
);

/* ─── SIDEBAR NAV ITEM ─── */
const SidebarItem = ({ item, activeSection, onNavigate, expanded, onToggle }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isActive = activeSection === item.id || (hasChildren && item.children.some(c => c.id === activeSection));
  const Icon = item.icon;

  return (
    <div>
      <button
        onClick={() => {
          if (hasChildren) { onToggle(item.id); }
          onNavigate(hasChildren ? item.children[0].id : item.id);
        }}
        data-testid={`sidebar-${item.id}`}
        className="w-full flex items-center gap-2.5 py-2.5 px-3 rounded-lg transition-all duration-200 text-left group"
        style={{
          background: isActive ? 'rgba(200,16,46,0.06)' : 'transparent',
          borderLeft: isActive ? '3px solid #C8102E' : '3px solid transparent',
        }}
      >
        <Icon size={15} color={isActive ? '#C8102E' : '#6B6B6F'} strokeWidth={1.5} className="shrink-0" />
        <span
          className="font-display text-xs font-semibold flex-1 truncate"
          style={{ color: isActive ? '#1C1C1E' : '#6B6B6F', letterSpacing: '0.01em' }}
        >
          {item.label}
        </span>
        {hasChildren && (
          <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown size={12} color={isActive ? '#C8102E' : '#9B9BA0'} />
          </motion.div>
        )}
      </button>

      <AnimatePresence>
        {hasChildren && expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="ml-5 pl-3 space-y-0.5 py-1" style={{ borderLeft: '1px solid #D4CFC7' }}>
              {item.children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => onNavigate(child.id)}
                  data-testid={`sidebar-${child.id}`}
                  className="w-full text-left py-1.5 px-2.5 rounded transition-colors duration-150"
                  style={{
                    background: activeSection === child.id ? 'rgba(200,16,46,0.04)' : 'transparent',
                  }}
                >
                  <span
                    className="font-body text-xs"
                    style={{
                      color: activeSection === child.id ? '#C8102E' : '#6B6B6F',
                      fontWeight: activeSection === child.id ? 600 : 400,
                      fontSize: '0.72rem',
                    }}
                  >
                    {child.label}
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── MOBILE NAV ─── */
const MobileNav = ({ activeSection, onNavigate, isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 lg:hidden"
        style={{ top: 60 }}
      >
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <motion.div
          initial={{ x: -300 }}
          animate={{ x: 0 }}
          exit={{ x: -300 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="absolute left-0 top-0 bottom-0 w-72 overflow-y-auto"
          style={{ background: '#FAF8F5', borderRight: '1px solid #D4CFC7' }}
        >
          <div className="p-4 space-y-1">
            <p className="font-mono mb-4 pb-3" style={{ fontSize: '0.6rem', color: '#C8102E', letterSpacing: '0.15em', textTransform: 'uppercase', borderBottom: '1px solid #D4CFC7' }}>
              Sections
            </p>
            {NAV_TREE.map((item) => (
              <MobileNavItem key={item.id} item={item} activeSection={activeSection} onNavigate={(id) => { onNavigate(id); onClose(); }} />
            ))}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const MobileNavItem = ({ item, activeSection, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const Icon = item.icon;

  return (
    <div>
      <button
        onClick={() => { if (hasChildren) setOpen(!open); onNavigate(hasChildren ? item.children[0].id : item.id); }}
        className="w-full flex items-center gap-2 py-2.5 px-2 rounded-lg text-left"
        style={{ background: activeSection === item.id ? 'rgba(200,16,46,0.05)' : 'transparent' }}
      >
        <Icon size={14} color="#6B6B6F" strokeWidth={1.5} />
        <span className="font-display text-xs font-semibold flex-1" style={{ color: '#1C1C1E' }}>{item.label}</span>
        {hasChildren && <ChevronDown size={12} color="#9B9BA0" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
      </button>
      {hasChildren && open && (
        <div className="ml-6 pl-3 space-y-0.5 py-1" style={{ borderLeft: '1px solid #D4CFC7' }}>
          {item.children.map((child) => (
            <button
              key={child.id}
              onClick={() => onNavigate(child.id)}
              className="w-full text-left py-1.5 px-2 rounded"
            >
              <span className="font-body" style={{ fontSize: '0.72rem', color: activeSection === child.id ? '#C8102E' : '#6B6B6F' }}>
                {child.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
const InvestorRelations = () => {
  const [activeSection, setActiveSection] = useState('corporate-profile');
  const [expandedItems, setExpandedItems] = useState({ 'corporate-governance': true });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  /* scroll-spy */
  useEffect(() => {
    const allIds = NAV_TREE.flatMap(item => item.children ? item.children.map(c => c.id) : [item.id]);
    const handleScroll = () => {
      for (let i = allIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(allIds[i]);
        if (el && el.getBoundingClientRect().top <= 140) {
          setActiveSection(allIds[i]);
          // auto-expand parent
          const parent = NAV_TREE.find(n => n.children?.some(c => c.id === allIds[i]));
          if (parent) setExpandedItems(prev => ({ ...prev, [parent.id]: true }));
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = useCallback((id) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  const toggleExpand = useCallback((id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#FAF8F5' }}>
      <PageSEO path="/investor-relations" />
      {/* ─── TOP NAV BAR ─── */}
      <nav
        data-testid="ir-navbar"
        style={{ background: 'rgba(28,28,30,0.95)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        className="fixed top-0 left-0 right-0 z-50"
      >
        <div className="container-main flex items-center justify-between" style={{ height: 60 }}>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 transition-colors" style={{ color: 'rgba(255,255,255,0.5)' }} data-testid="ir-back-home">
              <ArrowLeft size={16} />
              <span className="font-mono" style={{ fontSize: '0.62rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Home</span>
            </Link>
            <span style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
            <span className="font-display text-white" style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase' }}>QUALIANCE</span>
          </div>

          <div className="hidden lg:flex items-center gap-1">
            <span className="font-mono" style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Investor Relations
            </span>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="lg:hidden text-white flex items-center gap-2"
            data-testid="ir-mobile-nav-toggle"
          >
            <span className="font-mono" style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sections</span>
            {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      {/* Mobile sidebar */}
      <MobileNav activeSection={activeSection} onNavigate={scrollTo} isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      {/* ─── HERO ─── */}
      <section data-testid="ir-hero" style={{ background: '#1C1C1E', paddingTop: 100, paddingBottom: 40 }}>
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-mono block mb-3" style={{ fontSize: '0.62rem', color: '#C8102E', letterSpacing: '0.2em', textTransform: 'uppercase' }}>Investor Relations</span>
            <h1 className="font-display text-white mb-4" style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: 700, lineHeight: 1.15, maxWidth: 600 }}>
              Qualiance International Limited
            </h1>
            <p className="font-body" style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.5)', maxWidth: 550, lineHeight: 1.7 }}>
              Corporate governance, financial disclosures, and regulatory filings for stakeholders and investors.
            </p>
          </motion.div>
          {/* Quick jump tags */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="flex flex-wrap gap-2 mt-6"
          >
            {['Financials', 'Offer Document', 'Codes & Policies', 'Grievance Redressal'].map((tag) => (
              <button
                key={tag}
                onClick={() => {
                  const map = { 'Financials': 'results-reports', 'Offer Document': 'offer-draft-prospectus', 'Codes & Policies': 'codes-policies', 'Grievance Redressal': 'grievance-redressal' };
                  scrollTo(map[tag]);
                }}
                className="font-mono transition-all duration-200"
                style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.06)', padding: '5px 12px', borderRadius: 4, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(255,255,255,0.08)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(200,16,46,0.4)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
              >
                {tag}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── MAIN LAYOUT: SIDEBAR + CONTENT ─── */}
      <div className="container-main" style={{ paddingTop: 40, paddingBottom: 40 }}>
        <div className="flex gap-8">
          {/* SIDEBAR — desktop only */}
          <aside className="hidden lg:block shrink-0" style={{ width: 250 }}>
            <div className="sticky space-y-0.5" style={{ top: 80 }}>
              <p className="font-mono mb-4 pb-3" style={{ fontSize: '0.6rem', color: '#C8102E', letterSpacing: '0.15em', textTransform: 'uppercase', borderBottom: '1px solid #D4CFC7' }}>
                Sections
              </p>
              {NAV_TREE.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  activeSection={activeSection}
                  onNavigate={scrollTo}
                  expanded={!!expandedItems[item.id]}
                  onToggle={toggleExpand}
                />
              ))}
            </div>
          </aside>

          {/* CONTENT */}
          <main className="flex-1 min-w-0 space-y-12" data-testid="ir-content-area">
            {/* ═══ CORPORATE GOVERNANCE ═══ */}

            {/* Corporate Profile */}
            <section id="corporate-profile" data-testid="section-corporate-profile">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Corporate Governance</span>
              </div>
              <SectionCard title="Corporate Profile" testId="card-corporate-profile">
                <DocRow title="Corporate Profile" subtitle="Company overview and corporate information" href="/api/uploads/ir/corporate_profile.pdf" testId="doc-corporate-profile" />
                <DocRow title="Memorandum of Association (MOA)" subtitle="Constitutional document" href="/api/uploads/ir/moa_qualiance.pdf" testId="doc-moa" />
                <DocRow title="Articles of Association (AOA)" subtitle="Constitutional document" href="/api/uploads/ir/aoa_qualiance.pdf" testId="doc-aoa" />
              </SectionCard>
            </section>

            {/* BOD & Committees */}
            <section id="bod-committees" data-testid="section-bod-committees">
              <SectionCard title="Board of Directors & Committees" testId="card-bod-committees" description="Board composition, committee structure, and director profiles.">
                <DocRow title="Board of Directors & Committees" subtitle="Board composition and committee structure" href="/api/uploads/ir/bod_and_committees.pdf" testId="doc-bod-committees" />
                <DocRow title="Brief Profile of our Directors" subtitle="Board of Directors" href="/api/uploads/ir/brief_profile_directors.pdf" testId="doc-director-profiles" />
              </SectionCard>
            </section>

            {/* Codes & Policies */}
            <section id="codes-policies" data-testid="section-codes-policies">
              <SectionCard title="Codes & Policies" testId="card-codes-policies" description="Corporate governance codes, compliance policies, and regulatory frameworks governing the company.">
                {POLICIES.map((p, i) => (
                  <DocRow key={i} title={p.name} href={p.href} testId={`doc-policy-${i}`} />
                ))}
              </SectionCard>
            </section>

            {/* ═══ OFFER DOCUMENT ═══ */}
            <section id="offer-draft-prospectus" data-testid="section-offer-document" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Offer Document</span>
              </div>
              <SectionCard title="Offer Documents" testId="card-offer-documents" description="Prospectus documents and issue-related advertisements.">
                <DocRow title="Draft Abridged Prospectus" href="/api/uploads/ir/draft_prospectus_drhp.pdf" testId="doc-draft-prospectus" />
                <div id="offer-addendum-drhp"><DocRow title="Addendum to the Draft Abridged Prospectus" subtitle="Notice to investors, dated August 07, 2026" href="/api/uploads/ir/addendum_drhp.pdf" testId="doc-addendum-drhp" /></div>
                <div id="offer-rhp"><DocRow title="RHP (Red Herring Prospectus)" href="/api/uploads/ir/rhp_qualiance.pdf" testId="doc-rhp" /></div>
                <div id="offer-prospectus"><DocRow title="Prospectus" subtitle="Final Prospectus filed with the RoC" href="/api/uploads/ir/prospectus_qualiance.pdf" testId="doc-prospectus" /></div>
                <div id="offer-abridged"><DocRow title="Abridged Prospectus" href="/api/uploads/ir/abridged_prospectus.pdf" testId="doc-abridged-prospectus" /></div>
                <div id="offer-gid"><DocRow title="General Information Document (GID)" subtitle="General Information Document for investing in public issues" href="/api/uploads/ir/general_information_document_gid.pdf" testId="doc-gid" /></div>
                <div id="offer-issue-opening"><DocRow title="Issue Opening Advertisement" subtitle="Pre-issue &amp; price band advertisement" href="/api/uploads/ir/issue_opening_advertisement.pdf" testId="doc-issue-opening" /></div>
                <div id="offer-allotment"><DocRow title="Basis of Allotment Advertisement" subtitle="Post-issue basis of allotment advertisement" href="/api/uploads/ir/basis_of_allotment_advertisement.pdf" testId="doc-allotment" /></div>
                <div id="offer-public-announcement"><DocRow title="Public Announcement Advertisement" href="/api/uploads/ir/advertisement_qualiance.pdf" testId="doc-public-announcement-ad" /></div>
                <div id="offer-public-announcement-secondary"><DocRow title="Public Announcement – Secondary Sale" href="/api/uploads/ir/public_announcement_secondary_sale.pdf" testId="doc-public-announcement-secondary" /></div>
              </SectionCard>
            </section>

            {/* ═══ FINANCIALS ═══ */}
            <section id="results-reports" data-testid="section-financials" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Financials</span>
              </div>
              <SectionCard title="Results & Reports" testId="card-results-reports" description="Quarterly and annual financial results as per regulatory requirements.">
                <DocRow title="Un-Audited Financial Results with Limited Review Report – H1 ended 30-09-2026" subtitle="30-10-2026 · Reg. 33 of SEBI LODR" href={irFile('30-10-2026 - Un-Audited Financial Results with Limited Review Report - H1 ended 30-09-2026.pdf')} testId="doc-financial-results-h1-2026" />
              </SectionCard>
            </section>

            <section id="audited-financials" data-testid="section-audited-financials">
              <SectionCard title="Audited Financial Statements" testId="card-audited-financials" description="Audited financial statements of the Company for recent financial years.">
                {AUDITED_FINANCIALS.map((d, i) => (
                  <DocRow key={d.id} title={d.title} subtitle={materialSubtitle(d)} href={materialHref(d)} testId={`doc-audited-financial-${i}`} />
                ))}
              </SectionCard>
            </section>

            <section id="annual-reports" data-testid="section-annual-reports">
              <SectionCard title="Annual Reports" testId="card-annual-reports">
                {ANNUAL_REPORTS.map((item, i) => (
                  <DocRow key={i} title={`Annual Report ${item.year}`} href={item.href} testId={`doc-annual-report-${i}`} />
                ))}
              </SectionCard>
            </section>

            <section id="annual-returns" data-testid="section-annual-returns">
              <SectionCard title="Annual Returns" testId="card-annual-returns">
                {ANNUAL_RETURNS.map((item, i) => (
                  <DocRow key={i} title={`Annual Return ${item.year}`} href={item.href} testId={`doc-annual-return-${i}`} />
                ))}
              </SectionCard>
            </section>

            {/* ═══ INVESTOR PRESENTATION ═══ */}
            <section id="investor-presentation" data-testid="section-investor-presentation" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Investor Presentation</span>
              </div>
              <SectionCard title="Investor Presentation" testId="card-investor-presentation" description="Presentations providing insights into the company's strategy, performance, and outlook.">
                <DocRow title="Investor Presentation – H1 FY 2026-27" subtitle="30-10-2026" href={irFile('30-10-2026 - Investor Presentation - H1 FY 2026-27.pdf')} testId="doc-investor-pres-h1-2026" />
                <DocRow title="Investor Presentation" subtitle="Corporate presentation" href="/api/uploads/ir/investor_presentation.pdf" testId="doc-investor-pres" />
              </SectionCard>
            </section>

            {/* ═══ EARNINGS CALL ═══ */}
            <section id="earnings-call" data-testid="section-earnings-call" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Earnings Call</span>
              </div>
              <SectionCard title="Earnings Call" testId="card-earnings-call" description="Audio recordings of analyst and investor earnings calls.">
                <AudioRow title="Audio Recording Q1FY27 Earnings Call – 02 November 2026" subtitle="02-11-2026 · MP3" src={irFile('Audio_Recording_Q1FY27_Earnings_Call_02Nov2026.mp3')} testId="audio-earnings-call-q1fy27" />
                <DocRow title="Transcript of Q2 & H1 FY27 Earnings Call – 02 November 2026" subtitle="02-11-2026 · PDF" href={irFile('02-11-2026 - Transcript of Q2 & H1 FY27 Earnings Call.pdf')} testId="doc-earnings-call-transcript-q2fy27" />
              </SectionCard>
            </section>

            {/* ═══ STOCK EXCHANGE ═══ */}
            <section id="stock-exchange" data-testid="section-stock-exchange" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Stock Exchange</span>
              </div>
              <SectionCard title="Stock Exchange" testId="card-stock-exchange" description="Stock exchange listing information and filings.">
                <DocRow title="Integrated Filing (Governance) – Quarter ended 30-09-2026" subtitle="30-10-2026" href={irFile('30-10-2026 - Integrated Filing (Governance) - Quarter ended 30-09-2026.pdf')} testId="doc-integrated-filing-q2-2026" />
                <DocRow title="Certificate under Reg. 74(5) of SEBI (DP) Regulations – Quarter ended 30-09-2026" subtitle="10-10-2026" href={irFile('10-10-2026 - Certificate under Reg. 74(5) of SEBI (DP) Regulations - Quarter ended 30-09-2026.pdf')} testId="doc-reg74-q2-2026" />
              </SectionCard>
            </section>

            {/* ═══ OTHER DISCLOSURES ═══ */}
            <section id="disclosures-reg30" data-testid="section-disclosures" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Other Disclosures / Announcements</span>
              </div>
              <SectionCard title="Disclosures – Reg. 30" testId="card-disclosures" description="Disclosures under Regulation 30 of SEBI (Listing Obligations and Disclosure Requirements).">
                <DocRow title="Statement of Deviation or Variation (Reg. 32) – H1 ended 30-09-2026" subtitle="30-10-2026" href={irFile('30-10-2026 - Statement of Deviation or Variation (Reg. 32) - H1 ended 30-09-2026.pdf')} testId="doc-reg30-sod-h1-2026" />
                <DocRow title="Press Release – Financial Results H1 FY 2026-27" subtitle="30-10-2026" href={irFile('30-10-2026 - Press Release - Financial Results H1 FY 2026-27.pdf')} testId="doc-reg30-press-release-h1-2026" />
                <DocRow title="Appointment of Mr. Siddharth Gajra as CS &amp; Compliance Officer and Mr. Tushar Shridharani as Secretarial Auditor" subtitle="30-10-2026" href={irFile('30-10-2026 - Appointment of Mr. Siddharth Gajra as CS & Compliance Officer and Mr. Tushar Shridharani as Secretarial Auditor.pdf')} testId="doc-reg30-appointment-cs-auditor" />
                <DocRow title="Intimation of Earnings Call (Analyst – Investor Meet) on 02-11-2026" subtitle="26-10-2026" href={irFile('26-10-2026 - Intimation of Earnings Call (Analyst - Investor Meet) on 02-11-2026.pdf')} testId="doc-reg30-earnings-call" />
                <DocRow title="Appointment of Mrs. Dhriti Drolia as Non-Executive Director" subtitle="29-09-2026" href={irFile('29-09-2026 - Appointment of Mrs. Dhriti Drolia as Non-Executive Director.pdf')} testId="doc-reg30-appointment-dhriti" />
                <DocRow title="Resignation of Mrs. Krupa Rajesh Badani as Non-Executive Director" subtitle="21-09-2026" href={irFile('21-09-2026 - Resignation of Mrs. Krupa Rajesh Badani as Non-Executive Director.pdf')} testId="doc-reg30-resignation-krupa" />
                <DocRow title="Resignation of Mr. Pradeep Prajapati as Company Secretary and Compliance Officer" subtitle="21-09-2026" href={irFile('21-09-2026 - Resignation of Mr. Pradeep Prajapati as Company Secretary and Compliance Officer.pdf')} testId="doc-reg30-resignation-pradeep" />
              </SectionCard>
              <div id="material-creditors" className="pt-6">
                <SectionCard title="Material Creditors" testId="card-material-creditors" description="Details of the material creditors of the Company.">
                  <DocRow title="Material Creditors" subtitle="List of material creditors" href="/api/uploads/ir/material_creditors.pdf" testId="doc-material-creditors" />
                </SectionCard>
              </div>
            </section>

            {/* ═══ MATERIAL CONTRACTS & DOCUMENTS ═══ */}
            <section id="material-contracts" data-testid="section-material" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Material Contracts &amp; Documents</span>
              </div>
              {MATERIAL_INDEX.length > 0 && (
                <div className="mb-8" data-testid="material-index-block">
                  {MATERIAL_INDEX.map((d, i) => (
                    <DocRow key={d.id} title={d.title} subtitle={materialSubtitle(d)} href={materialHref(d)} testId={`doc-material-index-${i}`} />
                  ))}
                </div>
              )}
              <SectionCard title="Material Contracts" testId="card-material-contracts" description="Material contracts entered into by the Company in the ordinary course of business, available for inspection.">
                {MATERIAL_CONTRACTS.length === 0
                  ? <p className="font-body text-sm" style={{ color: '#9B9BA0' }} data-testid="material-contracts-empty">Documents will be published shortly.</p>
                  : MATERIAL_CONTRACTS.map((d, i) => (
                      <DocRow key={d.id} title={d.title} subtitle={materialSubtitle(d)} href={materialHref(d)} testId={`doc-material-contract-${i}`} />
                    ))}
              </SectionCard>
              <div id="material-documents" className="pt-6">
                <SectionCard title="Material Documents" testId="card-material-documents" description="Documents for inspection referred to in the offer document.">
                  {MATERIAL_DOCUMENTS.length === 0
                    ? <p className="font-body text-sm" style={{ color: '#9B9BA0' }} data-testid="material-documents-empty">Documents will be published shortly.</p>
                    : MATERIAL_DOCUMENTS.map((d, i) => (
                        <DocRow key={d.id} title={d.title} subtitle={materialSubtitle(d)} href={materialHref(d)} testId={`doc-material-document-${i}`} />
                      ))}
                </SectionCard>
              </div>
            </section>

            {/* ═══ NOTICE & UPDATES ═══ */}
            <section id="shareholding-pattern" data-testid="section-notice-updates" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Notice & Updates</span>
              </div>
              <SectionCard title="Shareholding Pattern" testId="card-shareholding">
                <DocRow title="Shareholding Pattern – Quarter ended 30-09-2026" subtitle="30-09-2026" href={irFile('30-09-2026 - Shareholding Pattern - Quarter ended 30-09-2026.pdf')} testId="doc-shareholding-q2-2026" />
                <DocRow title="Shareholding Pattern – Pre-listing (Reg. 31(1)(a))" subtitle="10-09-2026" href={irFile('10-09-2026 - Shareholding Pattern - Pre-listing (Reg. 31(1)(a)).pdf')} testId="doc-shareholding-prelisting" />
              </SectionCard>
            </section>

            <section id="board-meetings" data-testid="section-board-meetings">
              <SectionCard title="Board Meetings" testId="card-board-meetings">
                <DocRow title="Outcome of Board Meeting held on 30-10-2026" subtitle="30-10-2026" href={irFile('30-10-2026 - Outcome of Board Meeting held on 30-10-2026.pdf')} testId="doc-bm-outcome-30-10-2026" />
                <DocRow title="Intimation of Board Meeting to be held on 30-10-2026" subtitle="26-10-2026" href={irFile('26-10-2026 - Intimation of Board Meeting to be held on 30-10-2026.pdf')} testId="doc-bm-intimation-30-10-2026" />
              </SectionCard>
            </section>

            <section id="general-meetings" data-testid="section-general-meetings">
              <SectionCard title="General Meetings" testId="card-general-meetings">
                <DocRow title="Annual General Meeting (AGM)" testId="doc-agm" />
                <DocRow title="Extra-Ordinary General Meeting (EGM)" testId="doc-egm" />
                <DocRow title="Postal Ballot" testId="doc-postal-ballot" />
              </SectionCard>
            </section>

            <section id="notice" data-testid="section-notice">
              <SectionCard title="Notice" testId="card-notice">
                <DocRow title="Intimation of Closure of Trading Window" subtitle="28-09-2026" href={irFile('28-09-2026 - Intimation of Closure of Trading Window.pdf')} testId="doc-trading-window-closure" />
              </SectionCard>
            </section>

            {/* ═══ INVESTOR GRIEVANCE REDRESSAL ═══ */}
            <section id="grievance-redressal" data-testid="section-grievance-redressal" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Investor Support</span>
              </div>

            {/* ═══ GROUP COMPANY ═══ */}
            <section id="group-silvertraq" data-testid="section-group-company" className="pt-4">
              <div className="mb-6">
                <span className="section-label" style={{ fontSize: '0.7rem', marginBottom: '0.75rem' }}>Group Company</span>
              </div>
              <SectionCard title="Silvertraq International Private Limited" testId="card-group-silvertraq" description="Annual reports of group company Silvertraq International Private Limited.">
                <DocRow title="Annual Report 2024-25" subtitle="Silvertraq International Pvt. Ltd." href="/api/uploads/ir/silvertraq_annual_report_2024-25.pdf" testId="doc-silvertraq-2024-25" />
                <DocRow title="Annual Report 2023-24" subtitle="Silvertraq International Pvt. Ltd." href="/api/uploads/ir/silvertraq_annual_report_2023-24.pdf" testId="doc-silvertraq-2023-24" />
                <DocRow title="Annual Report 2022-23" subtitle="Silvertraq International Pvt. Ltd." href="/api/uploads/ir/silvertraq_annual_report_2022-23.pdf" testId="doc-silvertraq-2022-23" />
              </SectionCard>
            </section>
              <div className="card-light" style={{ padding: '2rem' }}>
                <h3 className="font-display text-lg font-bold mb-2" style={{ color: '#1C1C1E' }}>Investor Grievance Redressal</h3>
                <p className="font-body text-sm mb-6" style={{ color: '#6B6B6F', lineHeight: 1.7, maxWidth: 600 }}>
                  For any investor grievances, complaints, or queries regarding shares, dividends, annual reports, or any other investor-related matter, please contact:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Company Secretary / Compliance Officer */}
                  <div style={{ background: '#F5F0EB', borderRadius: 10, padding: '1.5rem', border: '1px solid #D4CFC7' }}>
                    <span className="font-mono block mb-3" style={{ fontSize: '0.6rem', color: '#C8102E', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>
                      Compliance Officer
                    </span>
                    <p className="font-body text-sm mb-3" style={{ color: '#3A3A3C', lineHeight: 1.7 }}>
                      Qualiance International Limited<br />
                      406 - B Wing, Knox Plaza, Mindspace<br />
                      Malad West, Mumbai 400064, INDIA
                    </p>
                    <div className="space-y-2">
                      <a href="tel:+912242666003" className="flex items-center gap-2" style={{ color: '#3A3A3C', textDecoration: 'none', fontSize: '0.85rem' }}>
                        <Phone size={13} color="#C8102E" /> +91 22 42666003
                      </a>
                      <a href="mailto:investor@qualiance.com" className="flex items-center gap-2" style={{ color: '#3A3A3C', textDecoration: 'none', fontSize: '0.85rem' }}>
                        <Mail size={13} color="#C8102E" /> investor@qualiance.com
                      </a>
                    </div>
                  </div>

                  {/* Registrar & Transfer Agent */}
                  <div style={{ background: '#F5F0EB', borderRadius: 10, padding: '1.5rem', border: '1px solid #D4CFC7' }}>
                    <span className="font-mono block mb-3" style={{ fontSize: '0.6rem', color: '#C8102E', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>
                      Registrar & Transfer Agent
                    </span>
                    <p className="font-display text-sm font-semibold mb-0.5" style={{ color: '#1C1C1E' }}>
                      MUFG Intime India Private Limited
                    </p>
                    <p className="font-body text-xs mb-3" style={{ color: '#6B6B6F', fontStyle: 'italic' }}>
                      (Formerly Link Intime India Private Limited)
                    </p>
                    <p className="font-body text-sm mb-3" style={{ color: '#3A3A3C', lineHeight: 1.7 }}>
                      C-101, Embassy 247, L B S Marg,<br />
                      Vikhroli (West), Mumbai-400083,<br />
                      Maharashtra, India
                    </p>
                    <div className="space-y-2">
                      <a href="tel:+918108114949" className="flex items-center gap-2" style={{ color: '#3A3A3C', textDecoration: 'none', fontSize: '0.85rem' }}>
                        <Phone size={13} color="#C8102E" /> +91 810 811 4949
                      </a>
                      <a href="mailto:qualiance.smeipo@in.mpms.mufg.com" className="flex items-center gap-2" style={{ color: '#3A3A3C', textDecoration: 'none', fontSize: '0.85rem' }}>
                        <Mail size={13} color="#C8102E" /> qualiance.smeipo@in.mpms.mufg.com
                      </a>
                      <a href="https://www.in.mpms.mufg.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2" style={{ color: '#3A3A3C', textDecoration: 'none', fontSize: '0.85rem' }}>
                        <ExternalLink size={13} color="#C8102E" /> www.in.mpms.mufg.com
                      </a>
                    </div>
                    <div className="mt-3 pt-3 space-y-1" style={{ borderTop: '1px solid #D4CFC7' }}>
                      <p className="font-mono" style={{ fontSize: '0.6rem', color: '#6B6B6F', letterSpacing: '0.03em' }}>
                        <span style={{ fontWeight: 600, color: '#3A3A3C' }}>Contact Person:</span> Shanti Gopalkrishnan
                      </p>
                      <p className="font-mono" style={{ fontSize: '0.6rem', color: '#6B6B6F', letterSpacing: '0.03em' }}>
                        <span style={{ fontWeight: 600, color: '#3A3A3C' }}>SEBI Reg. No.:</span> INR000004058
                      </p>
                      <p className="font-mono" style={{ fontSize: '0.6rem', color: '#6B6B6F', letterSpacing: '0.03em' }}>
                        <span style={{ fontWeight: 600, color: '#3A3A3C' }}>CIN:</span> U67190MH1999PTC118368
                      </p>
                    </div>
                  </div>
                </div>

                {/* SCORES section removed */}
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* ─── FOOTER ─── */}
      <footer style={{ background: '#1C1C1E', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '20px 0' }}>
        <div className="container-main flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="font-display text-white" style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', textDecoration: 'none' }}>
            QUALIANCE INTERNATIONAL LIMITED
          </Link>
          <p className="font-mono" style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em' }}>
            &copy; {new Date().getFullYear()} Qualiance International Limited. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default InvestorRelations;
